import { useRef, useState } from 'react';
import { useDismissOnOutsideClick } from '../../hooks/useDismissOnOutsideClick';
import { useAddTagSuggestions, useTagResources } from '../../hooks/useTags';
import { useTagRelated } from '../../stores/tagRelatedStore';

interface Props {
  projectId: string;
  /** Ids of the related resources currently checked (parents + children). */
  selectedIds: string[];
  /** Called after a successful tag so the caller can clear its selection. */
  onTagged: () => void;
}

/**
 * Bulk Add-Tag for the Related tab — React port of Angular's <iio-add-tag> with
 * hide-tag-related="true" (resource.html ~591). A tag typeahead + count-badged Tag
 * button that applies the tag to the checked parents/children. Unlike the grid's
 * AddTagControl this uses caller-supplied local selection (no shared selection store)
 * and has no all-matching mode. The "Tag Related" toggle is hidden but its shared value
 * still applies, as in Angular.
 */
export default function RelatedBulkTag({ projectId, selectedIds, onTagged }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const typeaheadRef = useRef<HTMLDivElement>(null);
  useDismissOnOutsideClick(typeaheadRef, open, () => setOpen(false));

  const { data: suggestions } = useAddTagSuggestions(projectId, query);
  const tagResources = useTagResources(projectId);
  const [tagRelated] = useTagRelated();

  const name = query.trim();
  const count = selectedIds.length;
  const canApply = count > 0 && name.length > 0 && !tagResources.isPending;

  const apply = () => {
    if (!canApply) return;
    tagResources.mutate(
      { name, tagRelated, resources: selectedIds },
      { onSuccess: () => { setQuery(''); setOpen(false); onTagged(); } }
    );
  };

  const shownSuggestions = query ? suggestions ?? [] : [];

  return (
    <div className="add-tag-control" data-testid="viewer-related-tag-panel">
      <div className="add-tag-row">
        <div className="add-tag-typeahead" ref={typeaheadRef}>
          <input
            type="text"
            className="form-control"
            aria-label="Tag name"
            data-testid="viewer-related-tag-input"
            value={query}
            disabled={tagResources.isPending}
            onChange={(e) => { setQuery(e.target.value); setOpen(e.target.value.length > 0); }}
            onFocus={() => setOpen(query.length > 0)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
          />
          {open && shownSuggestions.length > 0 && (
            <div className="dropdown-menu add-tag-suggestions" style={{ display: 'block' }} data-testid="viewer-related-tag-suggestion-list">
              {shownSuggestions.map((t) => (
                <div
                  key={t.id}
                  className="add-tag-suggestion"
                  data-testid="viewer-related-tag-suggestion-item"
                  onMouseDown={(e) => { e.preventDefault(); setQuery(t.name); setOpen(false); }}
                >
                  <i className="fas fa-fw fa-circle" style={{ color: t.color }} aria-hidden="true" /> {t.name}
                </div>
              ))}
            </div>
          )}
        </div>

        <button type="button" className="btn btn-primary" disabled={!canApply} onClick={apply} data-testid="viewer-related-tag-apply-btn">
          Tag <i className="fas fa-plus-circle" />
        </button>
      </div>
    </div>
  );
}
